import { useState, useRef, useCallback } from 'react';

const PREDEFINED_EFFECTS = [
  'relaxed',
  'energized',
  'focused',
  'sleepy',
  'hungry',
  'creative',
  'anxious',
  'pain relief',
  'nausea',
] as const;

const MAX_TOTAL_EFFECTS = 20;
const MAX_CUSTOM_TAG_LENGTH = 50;

interface EffectsSelectorProps {
  selected: string[];
  onChange: (effects: string[]) => void;
}

export function EffectsSelector({ selected, onChange }: EffectsSelectorProps) {
  const [customInput, setCustomInput] = useState('');
  const [customInputError, setCustomInputError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isAtLimit = selected.length >= MAX_TOTAL_EFFECTS;

  const toggleEffect = useCallback(
    (effect: string) => {
      if (selected.includes(effect)) {
        onChange(selected.filter((e) => e !== effect));
      } else {
        if (isAtLimit) return;
        onChange([...selected, effect]);
      }
    },
    [selected, onChange, isAtLimit]
  );

  const removeEffect = useCallback(
    (effect: string) => {
      onChange(selected.filter((e) => e !== effect));
    },
    [selected, onChange]
  );

  const addCustomEffect = useCallback(() => {
    const trimmed = customInput.trim();
    setCustomInputError(null);

    if (!trimmed) return;

    if (trimmed.length > MAX_CUSTOM_TAG_LENGTH) {
      setCustomInputError(`Custom effects must be ${MAX_CUSTOM_TAG_LENGTH} characters or fewer.`);
      return;
    }

    if (selected.includes(trimmed.toLowerCase()) || selected.includes(trimmed)) {
      setCustomInputError('This effect is already selected.');
      return;
    }

    // Check case-insensitive duplicate
    const lowerTrimmed = trimmed.toLowerCase();
    if (selected.some((e) => e.toLowerCase() === lowerTrimmed)) {
      setCustomInputError('This effect is already selected.');
      return;
    }

    if (isAtLimit) {
      setCustomInputError(`Maximum of ${MAX_TOTAL_EFFECTS} effects allowed.`);
      return;
    }

    onChange([...selected, trimmed]);
    setCustomInput('');
  }, [customInput, selected, onChange, isAtLimit]);

  const handleCustomInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomEffect();
    } else if (e.key === 'Escape') {
      setCustomInput('');
      setCustomInputError(null);
      inputRef.current?.blur();
    }
  };

  const handlePredefinedKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, effect: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleEffect(effect);
    }
  };

  // Separate predefined from custom in the selected list
  const predefinedSet = new Set<string>(PREDEFINED_EFFECTS);
  const customSelected = selected.filter((e) => !predefinedSet.has(e));

  return (
    <div className="effects-selector">
      <fieldset className="effects-predefined-group">
        <legend className="effects-legend">Effects</legend>
        <div className="effects-chips" role="group" aria-label="Predefined effects">
          {PREDEFINED_EFFECTS.map((effect) => {
            const isSelected = selected.includes(effect);
            return (
              <button
                key={effect}
                type="button"
                role="switch"
                aria-checked={isSelected}
                className={`effects-chip ${isSelected ? 'effects-chip-selected' : ''}`}
                onClick={() => toggleEffect(effect)}
                onKeyDown={(e) => handlePredefinedKeyDown(e, effect)}
                disabled={!isSelected && isAtLimit}
                aria-label={`${effect}${isSelected ? ' (selected)' : ''}`}
              >
                {effect}
              </button>
            );
          })}
        </div>
      </fieldset>

      <div className="effects-custom-input-group">
        <label htmlFor="effects-custom-input" className="effects-custom-label">
          Add custom effect
        </label>
        <div className="effects-custom-input-row">
          <input
            ref={inputRef}
            id="effects-custom-input"
            type="text"
            value={customInput}
            onChange={(e) => {
              setCustomInput(e.target.value);
              setCustomInputError(null);
            }}
            onKeyDown={handleCustomInputKeyDown}
            maxLength={MAX_CUSTOM_TAG_LENGTH}
            placeholder="Type a custom effect..."
            disabled={isAtLimit}
            aria-describedby={customInputError ? 'effects-custom-error' : 'effects-custom-hint'}
            aria-invalid={!!customInputError}
          />
          <button
            type="button"
            className="effects-add-button"
            onClick={addCustomEffect}
            disabled={isAtLimit || !customInput.trim()}
            aria-label="Add custom effect"
          >
            Add
          </button>
        </div>
        {customInputError && (
          <span id="effects-custom-error" className="field-error" role="alert">
            {customInputError}
          </span>
        )}
        {!customInputError && (
          <span id="effects-custom-hint" className="field-hint">
            {selected.length}/{MAX_TOTAL_EFFECTS} effects selected
          </span>
        )}
      </div>

      {customSelected.length > 0 && (
        <div className="effects-custom-tags" aria-label="Custom effects">
          {customSelected.map((effect) => (
            <span key={effect} className="effects-custom-tag">
              {effect}
              <button
                type="button"
                className="effects-tag-remove"
                onClick={() => removeEffect(effect)}
                aria-label={`Remove ${effect}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
